import { useEffect, useState } from "react";

import {
    ArrowButton, TextField,
    Select, AutocompleteMultipleSelect,
    TableMetadata, TableStateWithFilters,
    useTable,
    Typography,
    ColumnSortEnum
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { searchInstitutionNew } from "../../api/Institution/InstitutionApi";
import { getRoleList, searchUser } from "../../api/User/UserApi";
import { ContentArea, ListTable, SearchCard, ToolbarTop } from "../../components";
import { useAuth, useAuthCounties, useInput } from "../../hooks";
import { GreatGrandParentCodeBookPagedFilter } from "../../models";
import { InstitutionCodeBook, InstitutionCodeBookSearch } from "../../models/Institution";
import { ClaimType, User, UserSearch, UserSort, Role } from "../../models/User";
import { userDetailUrl, userEditUrl } from "../../routes/routes";
import { TableUtils } from "../../utils";

export function UserList() {
    const { roleId } = useParams<{ roleId: string }>();
    const { t } = useTranslation();
    const history = useHistory();
    const auth = useAuth();
    const authCounties = useAuthCounties();
    const { handleChange } = useInput();
    //STORAGE KEY
    const storageKey = 'UserSearchTableState';
    //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
    const createInitialTableState = () => {
        return TableUtils.CreateInitialTableState<UserSearch>(
            storageKey,
            TableUtils.tableDefaultState(UserSort.LastName, ColumnSortEnum.ASC)
        )
    }
    const [initialTableState] = useState<TableStateWithFilters<UserSearch>>(() => createInitialTableState());
    //INIT SEARCH FILTER
    const createInitialUserSearch = (): UserSearch => {
        return {
            oib: initialTableState.oib,
            firstName: initialTableState.firstName,
            lastName: initialTableState.lastName,
            roles: roleId ? [+roleId] : initialTableState.roles,
            county: initialTableState.county
                ? initialTableState.county
                : defaultCountyId,
            institution: initialTableState.institution
                ? initialTableState.institution
                : defaultInstitutionId,
        }
    }
    const defaultCountyId: number | undefined =
        auth.currentRole?.claimType === ClaimType.County &&
            (auth.currentRole?.locations?.length ?? 0) > 0
            ? auth!.currentRole!.locations![0]
            : undefined
    const defaultInstitutionId: number | undefined =
        auth.currentRole?.claimType === ClaimType.Institution
            && (auth.currentRole?.locations?.length ?? 0) > 0
            && auth.currentRole?.locations
            && auth.currentRole?.locations[0]
            ? auth!.currentRole!.locations![0]
            : undefined
    //TABLE DATA
    const defaultTableData = TableUtils.tableDefaultData<User>()
    const [tableData, setTableData] = useState<PagedResult<User>>(defaultTableData);
    //SET SEARCH FILTERS
    const [userSearch, setUserSearch] = useState<UserSearch>(() => createInitialUserSearch());
    const [tableSearch, setTableSearch] = useState<UserSearch>(() => createInitialUserSearch());
    const [filterChanged, setFilterChanged] = useState<boolean>(false);
    const [clearFilter, setClearFilter] = useState<boolean>(true);
    //TABLE METADATA
    const tableMetadata: TableMetadata<User> = {
        rowKey: "id",
        columns: [
            {
                columnKey: "oib",
                header: t("common.user.oib"),
                accessor: (item: User) => {
                    return (<div>{item.oib}</div>)
                },
                sortBy: UserSort.OIB,
            },
            {
                columnKey: "lastName",
                header: t("common.user.lastName"),
                accessor: (item: User) => {
                    return (<div>{item.lastName}</div>)
                },
                sortBy: UserSort.LastName,
            },
            {
                columnKey: "firstName",
                header: t("common.user.firstName"),
                accessor: (item: User) => {
                    return (<div>{item.firstName}</div>)
                },
                sortBy: UserSort.FirstName,
            },
            {
                columnKey: "roles",
                header: t("common.user.roles"),
                accessor: (item: User) => {
                    return (<div>{getRoleString(item)}</div>)
                },
            },
            {
                columnKey: "details",
                header: "",
                accessor: (item: User) => {
                    return (<div className={"text-right"}>
                        <ArrowButton
                            ariaLabel={t("common.details")}
                            onClick={() => handleDetailsClicked(item.id)}
                        />
                    </div>)
                }
            }
        ],
        rowToolTip: () => undefined
    };
    //SET TABLE
    const [tableProps, tableState, setFilters] = useTable<UserSearch>(
        {
            metadata: tableMetadata,
            data: tableData!.results,
            pagination: true,
            paginationPage: initialTableState.page,
            paginationPageSize: initialTableState.pageSize as any,
            totalItems: tableData!.rowCount,
            defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
        }
    );
    //FILTER DATA
    const [roles, setRoles] = useState<Role[]>([]);

    const [filteredInstitutions, setFilteredInstitutions] = useState<InstitutionCodeBook[]>([]);

    useEffect(() => {
        (async () => {
            var result = await getRoleList();
            if (result && result.length > 0) {
                setRoles(result);
            }
        })();
    }, []);

    useEffect(() => {
        if (clearFilter) {
            setClearFilter(false);
            setFilterChanged(false);
        }
        else
            setFilterChanged(true);
        TableUtils.RemoveSessionStorageItem(storageKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSearch]);

    useEffect(() => {
        (async () => {
            var request = TableUtils.GetSearchRequest<UserSearch>(tableSearch, tableState);
            var itemList = await searchUser(request);
            if (itemList !== null && itemList.results.length > 0) {
                setTableData(itemList);
            }
            else {
                setTableData(defaultTableData);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableState]);

    const handleDetailsClicked = (id?: number) => {
        if (id) {
            TableUtils.SetSessionStorageItem<UserSearch>(storageKey, tableSearch, tableState);
            history.push(
                userDetailUrl(
                    id!
                )
            );
        }
    };
    const handleNewClicked = () => {
        history.push(userEditUrl(0));
    };
    const search = () => {
        setFilters({ ...tableSearch });
    }
    const getRoleString = (a: User) => {
        var result = a.userRoles?.map((val, index) =>
            (<li><Typography component="span" variant="small">{val.role.name}</Typography></li>));
        return (<ul>{result}</ul>);
    };
    const handleCountyChange = (e: any) => {
        const item = Number.parseInt(e.target.value);
        const newSearch: UserSearch = {
            ...userSearch,
            county: !isNaN(item) ? item : undefined,
        };
        setUserSearch(newSearch);
    };

    const handleInstitutionFilterChange = async (filter: string) => {
        const search: InstitutionCodeBookSearch = {
            countyIds: userSearch?.county ? [userSearch?.county] : [],
            onlyActive: false,
            search: filter,
            includeOutsideEnrollmentArea: true,
        };
        var result = await searchInstitutionNew(search);
        setFilteredInstitutions(result);
    };
    const handleInstitutionChange = (e: any) => {
        //const institutionList = e.target.value as number[];
        const institutionList = e.target.value as CodeBook[];
        const newSearch: UserSearch = {
            ...userSearch,
            institution: institutionList && institutionList.length > 0 ? institutionList[0].id : undefined,
        };
        setUserSearch(newSearch);
    };

    return (
        <ContentArea>
            <ToolbarTop
                showAdd={"show"}
                showBack={"hide"}
                onClickAdd={handleNewClicked}
                pageTitle={t("navigation.userList")}
            >
            </ToolbarTop>
            <SearchCard
                onClickSearch={() => {
                    setTableSearch(userSearch)
                    setFilterChanged(false);
                    search();
                }}
                onClickClearSearch={() => {
                    setClearFilter(true);
                    setUserSearch({ county: defaultCountyId, roles: [] });
                    setTableSearch({ county: defaultCountyId, roles: [] })
                    search();
                }}
                isFilterChanged={filterChanged}
            >
                <Row >
                    <Col md={2}>
                        <TextField
                            label={t("common.user.oib")}
                            name={nameof<typeof userSearch>("oib")}
                            value={userSearch.oib}
                            onChange={handleChange(setUserSearch)}
                        />
                    </Col>
                    <Col md={2}>
                        <TextField
                            label={t("common.user.lastName")}
                            name={nameof<typeof userSearch>("lastName")}
                            value={userSearch.lastName}
                            onChange={handleChange(setUserSearch)}
                        />
                    </Col>
                    <Col md={2}>
                        <TextField
                            label={t("common.user.firstName")}
                            name={nameof<typeof userSearch>("firstName")}
                            value={userSearch.firstName}
                            onChange={handleChange(setUserSearch)}
                        />
                    </Col>
                    <Col md={6}>
                        <Select
                            multiple
                            label={t("common.user.roles")}
                            name={nameof<typeof userSearch>("roles")}
                            value={userSearch.roles}
                            onChange={handleChange(setUserSearch)}
                            selectData={codeBookToSelectData(roles)}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col md={6}>
                        <Select
                            label={t("common.user.county")}
                            name={nameof<typeof userSearch>("county")}
                            value={userSearch.county}
                            onChange={handleCountyChange}
                            selectData={codeBookToSelectData(authCounties)}
                            emptyOption={{ value: undefined, text: "" }}
                        />
                    </Col>
                    <Col md={6}>
                        <AutocompleteMultipleSelect<CodeBook>
                            label={t("common.user.institution")}
                            name={nameof<typeof userSearch>("institution")}
                            value={filteredInstitutions.filter(val => val.id === userSearch.institution)}
                            valueProp="id"
                            textProp="name"
                            onChange={handleInstitutionChange}
                            selectOptions={filteredInstitutions}
                            onFilterChange={handleInstitutionFilterChange}
                            maximumSelectedItems={1}
                        />
                    </Col>
                </Row>
            </SearchCard>
            <ListTable
                tableProps={tableProps}
            />
        </ContentArea>
    );
}